import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { botFilterAtom } from '../state/botFilterAtom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Flex,
  HStack,
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  useColorModeValue,
  Icon,
  TableContainer,
  Spinner,
  useToast,
  Progress,
  Skeleton,
  useToken,
  useDisclosure,
  Select,
  Tooltip,
} from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faEnvelope,
  faGlobe,
  faChartBar,
  faUsers,
  faMousePointer,
  faEye,
  faChevronRight,
  faChartLine,
  faChartPie,
  faBullseye,
  faLayerGroup,
  faRectangleAd,
  faTrophy,
  faArrowTrendUp,
  faArrowTrendDown,
  faPercent,
  faFilter,
  faCalendarDays,
  faTable,
  faArrowLeft,
  faCrosshairs,
} from '@fortawesome/free-solid-svg-icons';
import { analyticsApi } from '../api';
import DateRangePicker from '../components/common/DateRangePicker';
import { parseDateRange } from '../hooks/useAnalytics';
import { useChartAnimation } from '../hooks/useChartAnimation';
import { formatFullNumber } from '../utils/format';
import { ExportBuilderModal } from '../components/exports/ExportBuilderModal';
import { CHART_PALETTE, useChartColors, ChartPieTooltip } from '../components/common/chartTheme';

// Tab configuration for URL routing
const TAB_CONFIG = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'zones', label: 'By Zone' },
  { slug: 'ads', label: 'By Ad' },
  { slug: 'campaigns', label: 'By Campaign' },
];

function StatCardCompact({ icon, label, value, helpText, color, isLoading, trend, trendType }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="3px"
        bgGradient={`linear(to-r, ${color}.400, ${color}.600)`}
      />
      <CardBody>
        <Flex align="center" gap={4}>
          <Box
            p={2.5}
            bgGradient={`linear(135deg, ${color}.400 0%, ${color}.600 100%)`}
            borderRadius="xl"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={icon} />
            </Icon>
          </Box>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
            {isLoading ? (
              <Spinner size="sm" color={`${color}.500`} />
            ) : (
              <StatNumber
                fontSize={{ base: 'xl', md: '2xl' }}
                bgGradient={`linear(to-r, ${color}.500, ${color}.600)`}
                bgClip="text"
              >
                {value}
              </StatNumber>
            )}
            <HStack spacing={2}>
              <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>
              {trend && (
                <HStack spacing={1}>
                  <Icon
                    color={trendType === 'up' ? 'green.500' : 'red.500'}
                    boxSize={3}
                  >
                    <FontAwesomeIcon icon={trendType === 'up' ? faArrowTrendUp : faArrowTrendDown} />
                  </Icon>
                  <Text fontSize="xs" color={trendType === 'up' ? 'green.500' : 'red.500'} fontWeight="600">
                    {trend}
                  </Text>
                </HStack>
              )}
            </HStack>
          </Stat>
        </Flex>
      </CardBody>
    </Card>
  );
}

function PerformanceTrendChart({ data, loading, chartColors }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const [brandColor] = useToken('colors', ['brand.500']);
  const anim = useChartAnimation(loading);

  const { tooltipBg, tooltipText, gridColor, axisTextColor } = useChartColors();

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, brand.500 0%, purple.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faChartLine} />
            </Icon>
          </Box>
          <Heading size="md">Performance Trend</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <Skeleton height="200px" borderRadius="lg" />
        ) : data && data.length > 0 ? (
          <Box h="200px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={brandColor} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={brandColor} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: axisTextColor }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: axisTextColor }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: tooltipBg,
                    border: `1px solid ${gridColor}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: tooltipText,
                  }}
                  labelStyle={{ color: tooltipText }}
                />
                <Legend wrapperStyle={{ color: axisTextColor }} />
                <Area
                  {...anim}
                  type="monotone"
                  dataKey="impressions"
                  stroke={brandColor}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorImpressions)"
                  name="Impressions"
                />
                <Area
                  {...anim}
                  type="monotone"
                  dataKey="clicks"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorClicks)"
                  name="Clicks"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Flex justify="center" align="center" h="200px">
            <Text color={textSecondary}>No trend data available</Text>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
}

function CampaignComparisonChart({ data, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const anim = useChartAnimation(loading);

  const { tooltipBg, tooltipText, gridColor, axisTextColor } = useChartColors();

  const chartData = data?.slice(0, 6).map(c => ({
    name: c.name?.length > 12 ? c.name.substring(0, 12) + '...' : c.name,
    impressions: c.impressions || 0,
    clicks: c.clicks || 0,
    ctr: parseFloat(c.ctr) || 0,
  })) || [];

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faChartBar} />
            </Icon>
          </Box>
          <Heading size="md">Campaign Comparison</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <Skeleton height="200px" borderRadius="lg" />
        ) : chartData.length > 0 ? (
          <Box h="200px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: axisTextColor }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: axisTextColor }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: tooltipBg,
                    border: `1px solid ${gridColor}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: tooltipText,
                  }}
                  labelStyle={{ color: tooltipText }}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar {...anim} dataKey="impressions" fill="#6366f1" name="Impressions" radius={[4, 4, 0, 0]} />
                <Bar {...anim} dataKey="clicks" fill="#10b981" name="Clicks" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Flex justify="center" align="center" h="200px">
            <Text color={textSecondary}>No campaign data available</Text>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
}

function ZonePerformancePieChart({ data, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const anim = useChartAnimation(loading);

  const chartData = data?.slice(0, 5).map((z, i) => ({
    name: z.name?.length > 15 ? z.name.substring(0, 15) + '...' : z.name,
    value: z.impressions || 0,
    fill: CHART_PALETTE[i % CHART_PALETTE.length],
  })) || [];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, purple.500 0%, pink.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faChartPie} />
            </Icon>
          </Box>
          <Heading size="md">Zone Distribution</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <Skeleton height="200px" borderRadius="lg" />
        ) : chartData.length > 0 && total > 0 ? (
          <Flex align="center" justify="space-between" h="200px">
            <Box w="140px" h="140px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    {...anim}
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={2}
                    dataKey="value"
                  />
                  <RechartsTooltip content={<ChartPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <VStack spacing={2} align="stretch" flex={1} ml={4}>
              {chartData.map((zone, index) => (
                <Flex key={zone.name} justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="sm"
                      bg={zone.fill}
                    />
                    <Text fontSize="sm" noOfLines={1}>{zone.name}</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color={textSecondary}>
                    {((zone.value / total) * 100).toFixed(1)}%
                  </Text>
                </Flex>
              ))}
            </VStack>
          </Flex>
        ) : (
          <Flex justify="center" align="center" h="200px">
            <Text color={textSecondary}>No zone data available</Text>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
}

function TopPerformersCard({ campaigns, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const navigate = useNavigate();

  // Sort by CTR to get top performers
  const topPerformers = useMemo(() => {
    return [...(campaigns || [])]
      .sort((a, b) => parseFloat(b.ctr || 0) - parseFloat(a.ctr || 0))
      .slice(0, 5);
  }, [campaigns]);

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, orange.500 0%, red.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faTrophy} />
            </Icon>
          </Box>
          <Heading size="md">Top Performers (by CTR)</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <VStack spacing={3}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height="40px" borderRadius="lg" />
            ))}
          </VStack>
        ) : topPerformers.length > 0 ? (
          <VStack spacing={2} align="stretch">
            {topPerformers.map((campaign, index) => (
              <Flex
                key={`${campaign.name}-${index}`}
                justify="space-between"
                align="center"
                p={3}
                bg={hoverBg}
                borderRadius="lg"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ transform: 'translateX(4px)' }}
                onClick={() => navigate('/campaigns')}
              >
                <HStack spacing={3}>
                  <Flex
                    align="center"
                    justify="center"
                    w="28px"
                    h="28px"
                    borderRadius="full"
                    bgGradient={index === 0 ? 'linear(135deg, yellow.400, orange.500)' :
                      index === 1 ? 'linear(135deg, gray.300, gray.400)' :
                      index === 2 ? 'linear(135deg, orange.300, orange.500)' :
                      'linear(135deg, gray.200, gray.300)'}
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {index + 1}
                  </Flex>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                      {campaign.name}
                    </Text>
                    <Text fontSize="xs" color={textSecondary}>
                      {formatFullNumber(campaign.impressions || 0)} impressions
                    </Text>
                  </Box>
                </HStack>
                <Badge
                  colorScheme={parseFloat(campaign.ctr) > 2 ? 'green' : parseFloat(campaign.ctr) > 1 ? 'yellow' : 'gray'}
                  variant="solid"
                  borderRadius="full"
                  px={3}
                >
                  {campaign.ctr}% CTR
                </Badge>
              </Flex>
            ))}
          </VStack>
        ) : (
          <Text textAlign="center" color={textSecondary} py={8}>No campaign data available</Text>
        )}
      </CardBody>
    </Card>
  );
}

function CTRDistributionCard({ campaigns, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  // Calculate CTR distribution
  const distribution = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];

    const ranges = [
      { label: '0-1%', min: 0, max: 1, count: 0, color: 'red' },
      { label: '1-2%', min: 1, max: 2, count: 0, color: 'orange' },
      { label: '2-3%', min: 2, max: 3, count: 0, color: 'yellow' },
      { label: '3-5%', min: 3, max: 5, count: 0, color: 'green' },
      { label: '5%+', min: 5, max: 100, count: 0, color: 'brand' },
    ];

    campaigns.forEach(c => {
      const ctr = parseFloat(c.ctr) || 0;
      for (const range of ranges) {
        if (ctr >= range.min && ctr < range.max) {
          range.count++;
          break;
        }
      }
    });

    const total = campaigns.length;
    return ranges.map(r => ({
      ...r,
      percentage: total > 0 ? Math.round((r.count / total) * 100) : 0,
    }));
  }, [campaigns]);

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, green.500 0%, teal.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faPercent} />
            </Icon>
          </Box>
          <Heading size="md">CTR Distribution</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <VStack spacing={4}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height="24px" borderRadius="lg" />
            ))}
          </VStack>
        ) : distribution.length > 0 ? (
          <VStack spacing={3} align="stretch">
            {distribution.map((range) => (
              <Box key={range.label}>
                <Flex justify="space-between" mb={1}>
                  <HStack>
                    <Text fontSize="sm" fontWeight="500">{range.label}</Text>
                    <Badge colorScheme={range.color} variant="subtle" borderRadius="full" fontSize="xs">
                      {range.count} campaigns
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color={textSecondary}>
                    {range.percentage}%
                  </Text>
                </Flex>
                <Progress
                  value={range.percentage}
                  size="sm"
                  colorScheme={range.color}
                  borderRadius="full"
                  bg={borderColor}
                />
              </Box>
            ))}
          </VStack>
        ) : (
          <Text textAlign="center" color={textSecondary} py={8}>No data available</Text>
        )}
      </CardBody>
    </Card>
  );
}

// Type filter options
const TYPE_OPTIONS = [
  { value: 'all', label: 'All Sources', icon: faChartBar },
  { value: 'web', label: 'Website', icon: faGlobe },
  { value: 'email', label: 'Email', icon: faEnvelope },
];

function ReportsPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const filterBots = useRecoilValue(botFilterAtom);
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [zoneData, setZoneData] = useState([]);
  const [adData, setAdData] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAds, setExpandedAds] = useState({});
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  const [campaignDailyData, setCampaignDailyData] = useState([]);
  const [zoneDailyData, setZoneDailyData] = useState([]);
  const [adDailyData, setAdDailyData] = useState([]);
  // Drill-down filter state
  const [selectedFilter, setSelectedFilter] = useState(null); // { type: 'campaign'|'zone'|'ad', id: number, name: string }
  const [filteredTrendData, setFilteredTrendData] = useState([]);
  const [filteredDailyData, setFilteredDailyData] = useState([]);
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();

  const toast = useToast();

  // Get current tab index from URL param
  const tabIndex = useMemo(() => {
    const index = TAB_CONFIG.findIndex(t => t.slug === tab);
    return index >= 0 ? index : 0;
  }, [tab]);

  // Handle tab change - update URL
  const handleTabChange = (index) => {
    const newTab = TAB_CONFIG[index]?.slug || 'overview';
    navigate(`/reports/${newTab}`, { replace: true });
  };
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const expandedRowBg = useColorModeValue('gray.50', 'gray.700');
  const expandedRowHoverBg = useColorModeValue('gray.100', 'gray.600');

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const getDateParams = useCallback(() => {
    const dates = parseDateRange(dateRange);
    const params = { start: dates.start.toISOString(), end: dates.end.toISOString() };
    if (typeFilter && typeFilter !== 'all') {
      params.type = typeFilter;
    }
    if (selectedSite && selectedSite !== 'all') {
      params.site = selectedSite;
    }
    if (filterBots) {
      params.filterBots = 'true';
    }
    return params;
  }, [dateRange, typeFilter, selectedSite, filterBots]);

  // Format trend data for chart
  const formatTrendData = useCallback((data) => {
    return data.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      impressions: item.impressions || 0,
      clicks: item.clicks || 0,
    }));
  }, []);

  const toggleCampaignExpand = useCallback((campaignId) => {
    setExpandedCampaigns(prev => ({ ...prev, [campaignId]: !prev[campaignId] }));
  }, []);

  const toggleAdExpand = useCallback((adId) => {
    setExpandedAds(prev => ({ ...prev, [adId]: !prev[adId] }));
  }, []);

  // Handle drill-down filter selection
  const handleDrillDown = useCallback(async (type, id, name) => {
    setSelectedFilter({ type, id, name });
    const params = getDateParams();

    // Add filter param based on type
    const filterParams = { ...params };
    if (type === 'campaign') filterParams.campaignId = id;
    else if (type === 'zone') filterParams.zoneId = id;
    else if (type === 'ad') filterParams.adId = id;

    try {
      // Fetch filtered trend data
      const trendRes = await analyticsApi.getDailyTrend(filterParams);
      setFilteredTrendData(formatTrendData(trendRes.data || []));
      setFilteredDailyData(trendRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filtered data:', error);
      toast({
        title: 'Failed to load filtered data',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  }, [getDateParams, formatTrendData, toast]);

  // Clear drill-down filter
  const clearFilter = useCallback(() => {
    setSelectedFilter(null);
    setFilteredTrendData([]);
    setFilteredDailyData([]);
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setExpandedCampaigns({});
    setExpandedAds({});
    // Clear daily data to force refetch with new params
    setCampaignDailyData([]);
    setZoneDailyData([]);
    setAdDailyData([]);
    const params = getDateParams();

    try {
      const [overviewRes, zoneRes, adRes, campaignRes, trendRes] = await Promise.all([
        analyticsApi.getReportsOverview(params),
        analyticsApi.getReportsByZone(params),
        analyticsApi.getReportsByAdDetailed(params),
        analyticsApi.getReportsByCampaignDetailed(params),
        analyticsApi.getDailyTrend(params),
      ]);

      setOverview(overviewRes);
      setZoneData(zoneRes.data || []);
      setAdData(adRes.data || []);
      setCampaignData(campaignRes.data || []);
      setTrendData(formatTrendData(trendRes.data || []));

      // If daily breakdown is enabled, fetch daily data
      if (showDailyBreakdown) {
        const [campaignDailyRes, zoneDailyRes, adDailyRes] = await Promise.all([
          analyticsApi.getCampaignDaily(params),
          analyticsApi.getZoneDaily(params),
          analyticsApi.getAdDaily(params),
        ]);
        setCampaignDailyData(campaignDailyRes.data || []);
        setZoneDailyData(zoneDailyRes.data || []);
        setAdDailyData(adDailyRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast({
        title: 'Failed to load reports',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [getDateParams, toast, formatTrendData, showDailyBreakdown]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch daily data when breakdown toggle changes
  useEffect(() => {
    const fetchDailyData = async () => {
      if (showDailyBreakdown && campaignDailyData.length === 0) {
        const params = getDateParams();
        try {
          const [campaignDailyRes, zoneDailyRes, adDailyRes] = await Promise.all([
            analyticsApi.getCampaignDaily(params),
            analyticsApi.getZoneDaily(params),
            analyticsApi.getAdDaily(params),
          ]);
          setCampaignDailyData(campaignDailyRes.data || []);
          setZoneDailyData(zoneDailyRes.data || []);
          setAdDailyData(adDailyRes.data || []);
        } catch (error) {
          console.error('Failed to fetch daily data:', error);
        }
      }
    };
    fetchDailyData();
  }, [showDailyBreakdown, getDateParams, campaignDailyData.length]);

  // All exports (CSV, PDF, PowerPoint) handled by ExportBuilderModal

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <HStack spacing={3} align="center">
            <Heading
              size={{ base: 'lg', md: 'xl' }}
              bgGradient="linear(to-r, brand.500, accent.500)"
              bgClip="text"
              fontWeight="700"
            >
              Ad Reports
            </Heading>
            {selectedFilter && (
              <Badge
                colorScheme="brand"
                variant="solid"
                borderRadius="full"
                px={3}
                py={1}
                fontSize="sm"
              >
                <HStack spacing={2}>
                  <Icon boxSize={3}>
                    <FontAwesomeIcon icon={faCrosshairs} />
                  </Icon>
                  <Text>
                    {selectedFilter.type === 'campaign' ? 'Campaign' : selectedFilter.type === 'zone' ? 'Zone' : 'Ad'}: {selectedFilter.name}
                  </Text>
                </HStack>
              </Badge>
            )}
          </HStack>
          <Text color={textSecondary} mt={1}>
            {selectedFilter
              ? `Viewing detailed stats for ${selectedFilter.name}`
              : 'Comprehensive ad performance reports'
            }
          </Text>
        </Box>
        <Flex direction={{ base: 'column', xl: 'row' }} gap={3} align={{ base: 'flex-start', xl: 'center' }}>
          {selectedFilter && (
            <Button
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              size="sm"
              variant="outline"
              colorScheme="brand"
              borderRadius="xl"
              onClick={clearFilter}
            >
              Back to All
            </Button>
          )}
          <HStack spacing={2}>
            <Box
              p={2}
              bg={typeFilter === 'email' ? 'purple.100' : typeFilter === 'web' ? 'blue.100' : 'gray.100'}
              borderRadius="lg"
              display={{ base: 'none', md: 'flex' }}
            >
              <Icon
                color={typeFilter === 'email' ? 'purple.500' : typeFilter === 'web' ? 'blue.500' : 'gray.500'}
                boxSize={4}
              >
                <FontAwesomeIcon icon={typeFilter === 'email' ? faEnvelope : typeFilter === 'web' ? faGlobe : faFilter} />
              </Icon>
            </Box>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              size="sm"
              borderRadius="xl"
              w="140px"
              bg={cardBg}
            >
              {TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </HStack>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={true}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
            size="sm"
          />
          <Button
            leftIcon={<FontAwesomeIcon icon={showDailyBreakdown ? faTable : faCalendarDays} />}
            size="sm"
            variant={showDailyBreakdown ? 'solid' : 'outline'}
            colorScheme={showDailyBreakdown ? 'brand' : 'gray'}
            borderRadius="xl"
            onClick={() => setShowDailyBreakdown(!showDailyBreakdown)}
          >
            {showDailyBreakdown ? 'Daily View' : 'Daily Breakdown'}
          </Button>
          <Button
            leftIcon={<FontAwesomeIcon icon={faDownload} />}
            size="sm"
            variant="outline"
            borderRadius="xl"
            onClick={onExportOpen}
          >
            Export
          </Button>
        </Flex>
      </Flex>

      <Tabs colorScheme="brand" variant="soft-rounded" index={tabIndex} onChange={handleTabChange}>
        <TabList
          flexWrap="wrap"
          gap={2}
          bg={cardBg}
          p={2}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          mb={6}
        >
          <Tab borderRadius="xl" fontSize="sm">Overview</Tab>
          <Tab borderRadius="xl" fontSize="sm">By Zone</Tab>
          <Tab borderRadius="xl" fontSize="sm">By Ad</Tab>
          <Tab borderRadius="xl" fontSize="sm">By Campaign</Tab>
        </TabList>

        <TabPanels>
          {/* Enhanced Overview */}
          <TabPanel px={0}>
            {/* Stats Cards */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
              <StatCardCompact
                icon={faEye}
                label="Total Impressions"
                value={formatFullNumber(overview?.totalImpressions)}
                helpText="All ad views"
                color="brand"
                isLoading={isLoading}
              />
              <StatCardCompact
                icon={faMousePointer}
                label="Total Clicks"
                value={formatFullNumber(overview?.totalClicks)}
                helpText={`${overview?.ctr || 0}% CTR`}
                color="green"
                isLoading={isLoading}
              />
              <StatCardCompact
                icon={faUsers}
                label="Unique Visitors"
                value={formatFullNumber(overview?.uniqueVisitors)}
                helpText="Across all sites"
                color="purple"
                isLoading={isLoading}
              />
              <StatCardCompact
                icon={faChartBar}
                label="Page Views"
                value={formatFullNumber(overview?.pageViews)}
                helpText={`${overview?.pagesPerVisit || 0} pages/visit`}
                color="cyan"
                isLoading={isLoading}
              />
            </SimpleGrid>

            {/* Charts Row */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
              <PerformanceTrendChart data={selectedFilter ? filteredTrendData : trendData} loading={isLoading} />
              <CampaignComparisonChart data={campaignData} loading={isLoading} />
            </SimpleGrid>

            {/* Second Charts Row */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
              <ZonePerformancePieChart data={zoneData} loading={isLoading} />
              <TopPerformersCard campaigns={campaignData} loading={isLoading} />
              <CTRDistributionCard campaigns={campaignData} loading={isLoading} />
            </SimpleGrid>

            {/* Filtered Daily Breakdown or Top Campaigns Table */}
            {selectedFilter ? (
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faCrosshairs} />
                        </Icon>
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Heading size="md">{selectedFilter.name}</Heading>
                        <Text fontSize="sm" color={textSecondary}>
                          Daily breakdown for this {selectedFilter.type}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                      onClick={clearFilter}
                    >
                      Back
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  {filteredDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredDailyData.map((day, idx) => (
                            <Tr key={idx} _hover={{ bg: hoverBg }} transition="all 0.2s">
                              <Td>
                                <HStack>
                                  <Icon color="brand.400" boxSize={3}>
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                  </Icon>
                                  <Text fontWeight="500">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td isNumeric fontWeight="600">{formatFullNumber(day.impressions)}</Td>
                              <Td isNumeric>{formatFullNumber(day.clicks)}</Td>
                              <Td isNumeric>
                                <Badge colorScheme="brand" variant="subtle" borderRadius="full">{day.ctr}%</Badge>
                              </Td>
                            </Tr>
                          ))}
                          {/* Totals row */}
                          <Tr bg={hoverBg} fontWeight="bold">
                            <Td>
                              <Text fontWeight="700">Total</Text>
                            </Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.impressions, 0))}
                            </Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.clicks, 0))}
                            </Td>
                            <Td isNumeric>
                              {(() => {
                                const totalImp = filteredDailyData.reduce((sum, d) => sum + d.impressions, 0);
                                const totalClk = filteredDailyData.reduce((sum, d) => sum + d.clicks, 0);
                                const ctr = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
                                return <Badge colorScheme="green" variant="solid" borderRadius="full">{ctr}%</Badge>;
                              })()}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            ) : (
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faBullseye} />
                      </Icon>
                    </Box>
                    <Heading size="md">All Campaigns</Heading>
                    <Text fontSize="sm" color={textSecondary} ml={2}>Click a row to drill down</Text>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  {isLoading ? (
                    <Flex justify="center" py={8}>
                      <Spinner size="lg" color="brand.500" />
                    </Flex>
                  ) : campaignData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Campaign</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                            <Th display={{ base: 'none', lg: 'table-cell' }}>Performance</Th>
                            <Th w="80px"></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {campaignData.slice(0, 10).map((campaign, idx) => {
                            const ctrValue = parseFloat(campaign.ctr) || 0;
                            const maxImpressions = Math.max(...campaignData.map(c => c.impressions || 0));
                            const percentage = maxImpressions > 0 ? ((campaign.impressions || 0) / maxImpressions) * 100 : 0;
                            return (
                              <Tr
                                key={`${campaign.name}-${idx}`}
                                _hover={{ bg: hoverBg }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => handleDrillDown('campaign', campaign.id, campaign.name)}
                              >
                                <Td>
                                  <HStack>
                                    <Box p={2} bg={campaign.channel === 'web' ? 'blue.100' : 'purple.100'} borderRadius="lg">
                                      <Icon color={campaign.channel === 'web' ? 'blue.500' : 'purple.500'} boxSize={4}>
                                        <FontAwesomeIcon icon={campaign.channel === 'web' ? faGlobe : faEnvelope} />
                                      </Icon>
                                    </Box>
                                    <Text fontWeight="500">{campaign.name}</Text>
                                  </HStack>
                                </Td>
                                <Td isNumeric fontWeight="600">{formatFullNumber(campaign.impressions)}</Td>
                                <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(campaign.clicks)}</Td>
                                <Td isNumeric>
                                  <Badge
                                    colorScheme={ctrValue > 2 ? 'green' : ctrValue > 1 ? 'yellow' : 'gray'}
                                    variant="subtle"
                                    borderRadius="full"
                                  >
                                    {campaign.ctr}%
                                  </Badge>
                                </Td>
                                <Td display={{ base: 'none', lg: 'table-cell' }}>
                                  <Progress
                                    value={percentage}
                                    size="sm"
                                    colorScheme="brand"
                                    borderRadius="full"
                                    bg={borderColor}
                                    w="100px"
                                  />
                                </Td>
                                <Td>
                                  <Tooltip label="View daily breakdown" placement="left">
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="brand"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDrillDown('campaign', campaign.id, campaign.name);
                                      }}
                                    >
                                      <Icon boxSize={3}>
                                        <FontAwesomeIcon icon={faCrosshairs} />
                                      </Icon>
                                    </Button>
                                  </Tooltip>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            )}
          </TabPanel>

          {/* By Zone - Enhanced */}
          <TabPanel px={0}>
            {selectedFilter ? (
              // Filtered daily breakdown view
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, purple.500 0%, blue.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faCrosshairs} />
                        </Icon>
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Heading size="md">{selectedFilter.name}</Heading>
                        <Text fontSize="sm" color={textSecondary}>
                          Daily breakdown for this {selectedFilter.type}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                      onClick={clearFilter}
                    >
                      Back
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  {filteredDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredDailyData.map((day, idx) => (
                            <Tr key={idx} _hover={{ bg: hoverBg }} transition="all 0.2s">
                              <Td>
                                <HStack>
                                  <Icon color="purple.400" boxSize={3}>
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                  </Icon>
                                  <Text fontWeight="500">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td isNumeric fontWeight="600">{formatFullNumber(day.impressions)}</Td>
                              <Td isNumeric>{formatFullNumber(day.clicks)}</Td>
                              <Td isNumeric>
                                <Badge colorScheme="purple" variant="subtle" borderRadius="full">{day.ctr}%</Badge>
                              </Td>
                            </Tr>
                          ))}
                          <Tr bg={hoverBg} fontWeight="bold">
                            <Td><Text fontWeight="700">Total</Text></Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.impressions, 0))}
                            </Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.clicks, 0))}
                            </Td>
                            <Td isNumeric>
                              {(() => {
                                const totalImp = filteredDailyData.reduce((sum, d) => sum + d.impressions, 0);
                                const totalClk = filteredDailyData.reduce((sum, d) => sum + d.clicks, 0);
                                const ctr = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
                                return <Badge colorScheme="green" variant="solid" borderRadius="full">{ctr}%</Badge>;
                              })()}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                  <StatCardCompact
                    icon={faLayerGroup}
                    label="Total Zones"
                    value={zoneData.length}
                    helpText="Active zones"
                    color="purple"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faEye}
                    label="Zone Impressions"
                    value={formatFullNumber(zoneData.reduce((sum, z) => sum + (z.impressions || 0), 0))}
                    helpText="All zones"
                    color="brand"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faMousePointer}
                    label="Zone Clicks"
                    value={formatFullNumber(zoneData.reduce((sum, z) => sum + (z.clicks || 0), 0))}
                    helpText="Total clicks"
                    color="green"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faPercent}
                    label="Avg Zone CTR"
                    value={`${zoneData.length > 0 ? (zoneData.reduce((sum, z) => sum + parseFloat(z.ctr || 0), 0) / zoneData.length).toFixed(2) : 0}%`}
                    helpText="Average"
                    color="cyan"
                    isLoading={isLoading}
                  />
                </SimpleGrid>

                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                  <CardBody>
                {isLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" color="brand.500" />
                  </Flex>
                ) : showDailyBreakdown ? (
                  // Daily breakdown view
                  zoneDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily zone data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th w="40px"></Th>
                            <Th>Zone</Th>
                            <Th display={{ base: 'none', md: 'table-cell' }}>Site</Th>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {zoneDailyData.map((zone) => {
                            const isExpanded = expandedCampaigns[`zone-${zone.id}`];
                            const hasDaily = zone.daily && zone.daily.length > 0;
                            return (
                              <>
                                <Tr
                                  key={`zone-daily-${zone.id}`}
                                  _hover={{ bg: hoverBg }}
                                  transition="all 0.2s"
                                  cursor={hasDaily ? 'pointer' : 'default'}
                                  onClick={() => hasDaily && toggleCampaignExpand(`zone-${zone.id}`)}
                                  fontWeight="600"
                                  bg={hoverBg}
                                >
                                  <Td w="40px" px={2}>
                                    {hasDaily && (
                                      <Icon
                                        color={textSecondary}
                                        boxSize={3}
                                        transition="transform 0.2s"
                                        transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                                      >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                      </Icon>
                                    )}
                                  </Td>
                                  <Td>
                                    <HStack>
                                      <Box p={2} bg="purple.100" borderRadius="lg">
                                        <Icon color="purple.500" boxSize={3}>
                                          <FontAwesomeIcon icon={faLayerGroup} />
                                        </Icon>
                                      </Box>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="600">{zone.name}</Text>
                                        {hasDaily && (
                                          <Text fontSize="xs" color={textSecondary}>
                                            {zone.daily.length} day{zone.daily.length !== 1 ? 's' : ''} with data
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                  </Td>
                                  <Td display={{ base: 'none', md: 'table-cell' }}>{zone.site}</Td>
                                  <Td>
                                    <Badge colorScheme="gray" variant="subtle" borderRadius="full">Total</Badge>
                                  </Td>
                                  <Td isNumeric fontWeight="600">{formatFullNumber(zone.totalImpressions)}</Td>
                                  <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(zone.totalClicks)}</Td>
                                  <Td isNumeric>
                                    <Badge colorScheme="brand" variant="subtle" borderRadius="full">{zone.ctr}%</Badge>
                                  </Td>
                                </Tr>
                                {isExpanded && zone.daily?.map((day, idx) => (
                                  <Tr
                                    key={`zone-${zone.id}-day-${idx}`}
                                    bg={expandedRowBg}
                                    _hover={{ bg: expandedRowHoverBg }}
                                  >
                                    <Td w="40px" px={2}></Td>
                                    <Td pl={10}></Td>
                                    <Td display={{ base: 'none', md: 'table-cell' }}></Td>
                                    <Td>
                                      <HStack>
                                        <Icon color="purple.400" boxSize={3}>
                                          <FontAwesomeIcon icon={faCalendarDays} />
                                        </Icon>
                                        <Text fontSize="sm">
                                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </Text>
                                      </HStack>
                                    </Td>
                                    <Td isNumeric fontSize="sm">{formatFullNumber(day.impressions)}</Td>
                                    <Td isNumeric fontSize="sm" display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(day.clicks)}</Td>
                                    <Td isNumeric>
                                      <Badge colorScheme="gray" variant="outline" borderRadius="full" fontSize="xs">{day.ctr}%</Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )
                ) : zoneData.length === 0 ? (
                  <Text textAlign="center" color={textSecondary} py={8}>No zone data available</Text>
                ) : (
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Zone</Th>
                          <Th display={{ base: 'none', md: 'table-cell' }}>Site</Th>
                          <Th display={{ base: 'none', lg: 'table-cell' }}>Size</Th>
                          <Th isNumeric>Impressions</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                          <Th isNumeric>CTR</Th>
                          <Th w="80px"></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {zoneData.map((zone) => (
                          <Tr
                            key={zone.id}
                            _hover={{ bg: hoverBg }}
                            transition="all 0.2s"
                            cursor="pointer"
                            onClick={() => handleDrillDown('zone', zone.id, zone.name)}
                          >
                            <Td>
                              <HStack>
                                <Box p={2} bg="purple.100" borderRadius="lg">
                                  <Icon color="purple.500" boxSize={3}>
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                  </Icon>
                                </Box>
                                <Text fontWeight="600">{zone.name}</Text>
                              </HStack>
                            </Td>
                            <Td display={{ base: 'none', md: 'table-cell' }}>{zone.site}</Td>
                            <Td display={{ base: 'none', lg: 'table-cell' }}>
                              <Badge variant="outline" borderRadius="full">{zone.size}</Badge>
                            </Td>
                            <Td isNumeric fontWeight="600">{formatFullNumber(zone.impressions)}</Td>
                            <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(zone.clicks)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="brand" variant="subtle" borderRadius="full">{zone.ctr}%</Badge>
                            </Td>
                            <Td>
                              <Tooltip label="View daily breakdown" placement="left">
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="purple"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDrillDown('zone', zone.id, zone.name);
                                  }}
                                >
                                  <Icon boxSize={3}>
                                    <FontAwesomeIcon icon={faCrosshairs} />
                                  </Icon>
                                </Button>
                              </Tooltip>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
                  </CardBody>
                </Card>
              </>
            )}
          </TabPanel>

          {/* By Ad - Enhanced */}
          <TabPanel px={0}>
            {selectedFilter ? (
              // Filtered daily breakdown view
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, pink.500 0%, purple.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faCrosshairs} />
                        </Icon>
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Heading size="md">{selectedFilter.name}</Heading>
                        <Text fontSize="sm" color={textSecondary}>
                          Daily breakdown for this {selectedFilter.type}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                      onClick={clearFilter}
                    >
                      Back
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  {filteredDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredDailyData.map((day, idx) => (
                            <Tr key={idx} _hover={{ bg: hoverBg }} transition="all 0.2s">
                              <Td>
                                <HStack>
                                  <Icon color="pink.400" boxSize={3}>
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                  </Icon>
                                  <Text fontWeight="500">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td isNumeric fontWeight="600">{formatFullNumber(day.impressions)}</Td>
                              <Td isNumeric>{formatFullNumber(day.clicks)}</Td>
                              <Td isNumeric>
                                <Badge colorScheme="pink" variant="subtle" borderRadius="full">{day.ctr}%</Badge>
                              </Td>
                            </Tr>
                          ))}
                          <Tr bg={hoverBg} fontWeight="bold">
                            <Td><Text fontWeight="700">Total</Text></Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.impressions, 0))}
                            </Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.clicks, 0))}
                            </Td>
                            <Td isNumeric>
                              {(() => {
                                const totalImp = filteredDailyData.reduce((sum, d) => sum + d.impressions, 0);
                                const totalClk = filteredDailyData.reduce((sum, d) => sum + d.clicks, 0);
                                const ctr = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
                                return <Badge colorScheme="green" variant="solid" borderRadius="full">{ctr}%</Badge>;
                              })()}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                  <StatCardCompact
                    icon={faRectangleAd}
                    label="Total Ads"
                    value={adData.length}
                    helpText="Active ads"
                    color="pink"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faEye}
                    label="Ad Impressions"
                    value={formatFullNumber(adData.reduce((sum, a) => sum + (a.impressions || 0), 0))}
                    helpText="All ads"
                    color="brand"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faMousePointer}
                    label="Ad Clicks"
                    value={formatFullNumber(adData.reduce((sum, a) => sum + (a.clicks || 0), 0))}
                    helpText="Total clicks"
                    color="green"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faPercent}
                    label="Avg Ad CTR"
                    value={`${adData.length > 0 ? (adData.reduce((sum, a) => sum + parseFloat(a.ctr || 0), 0) / adData.length).toFixed(2) : 0}%`}
                    helpText="Average"
                    color="orange"
                    isLoading={isLoading}
                  />
                </SimpleGrid>

                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                  <CardBody>
                    {isLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" color="brand.500" />
                  </Flex>
                ) : showDailyBreakdown ? (
                  // Daily breakdown view
                  adDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily ad data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th w="40px"></Th>
                            <Th>Ad</Th>
                            <Th display={{ base: 'none', lg: 'table-cell' }}>Campaign</Th>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {adDailyData.map((ad) => {
                            const isExpanded = expandedAds[ad.id];
                            const hasDaily = ad.daily && ad.daily.length > 0;
                            return (
                              <>
                                <Tr
                                  key={`ad-daily-${ad.id}`}
                                  _hover={{ bg: hoverBg }}
                                  transition="all 0.2s"
                                  cursor={hasDaily ? 'pointer' : 'default'}
                                  onClick={() => hasDaily && toggleAdExpand(ad.id)}
                                  fontWeight="600"
                                  bg={hoverBg}
                                >
                                  <Td w="40px" px={2}>
                                    {hasDaily && (
                                      <Icon
                                        color={textSecondary}
                                        boxSize={3}
                                        transition="transform 0.2s"
                                        transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                                      >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                      </Icon>
                                    )}
                                  </Td>
                                  <Td>
                                    <HStack>
                                      <Box p={2} bg="pink.100" borderRadius="lg">
                                        <Icon color="pink.500" boxSize={3}>
                                          <FontAwesomeIcon icon={faRectangleAd} />
                                        </Icon>
                                      </Box>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="600">{ad.name}</Text>
                                        {hasDaily && (
                                          <Text fontSize="xs" color={textSecondary}>
                                            {ad.daily.length} day{ad.daily.length !== 1 ? 's' : ''} with data
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                  </Td>
                                  <Td display={{ base: 'none', lg: 'table-cell' }}>
                                    <Text fontSize="sm" color={textSecondary}>{ad.campaign}</Text>
                                  </Td>
                                  <Td>
                                    <Badge colorScheme="gray" variant="subtle" borderRadius="full">Total</Badge>
                                  </Td>
                                  <Td isNumeric fontWeight="600">{formatFullNumber(ad.totalImpressions)}</Td>
                                  <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(ad.totalClicks)}</Td>
                                  <Td isNumeric>
                                    <Badge colorScheme="brand" variant="subtle" borderRadius="full">{ad.ctr}%</Badge>
                                  </Td>
                                </Tr>
                                {isExpanded && ad.daily?.map((day, idx) => (
                                  <Tr
                                    key={`ad-${ad.id}-day-${idx}`}
                                    bg={expandedRowBg}
                                    _hover={{ bg: expandedRowHoverBg }}
                                  >
                                    <Td w="40px" px={2}></Td>
                                    <Td pl={10}></Td>
                                    <Td display={{ base: 'none', lg: 'table-cell' }}></Td>
                                    <Td>
                                      <HStack>
                                        <Icon color="pink.400" boxSize={3}>
                                          <FontAwesomeIcon icon={faCalendarDays} />
                                        </Icon>
                                        <Text fontSize="sm">
                                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </Text>
                                      </HStack>
                                    </Td>
                                    <Td isNumeric fontSize="sm">{formatFullNumber(day.impressions)}</Td>
                                    <Td isNumeric fontSize="sm" display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(day.clicks)}</Td>
                                    <Td isNumeric>
                                      <Badge colorScheme="gray" variant="outline" borderRadius="full" fontSize="xs">{day.ctr}%</Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )
                ) : adData.length === 0 ? (
                  <Text textAlign="center" color={textSecondary} py={8}>No ad data available</Text>
                ) : (
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th w="40px"></Th>
                          <Th>Ad</Th>
                          <Th display={{ base: 'none', lg: 'table-cell' }}>Type</Th>
                          <Th isNumeric>Impressions</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                          <Th isNumeric>CTR</Th>
                          <Th w="80px"></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {adData.map((ad) => {
                          const isExpanded = expandedAds[ad.id];
                          const hasBreakdowns = ad.breakdowns && ad.breakdowns.length > 0;
                          return (
                            <>
                              <Tr
                                key={`ad-${ad.id}`}
                                _hover={{ bg: hoverBg }}
                                transition="all 0.2s"
                                cursor="pointer"
                                onClick={() => hasBreakdowns ? toggleAdExpand(ad.id) : handleDrillDown('ad', ad.id, ad.name)}
                              >
                                <Td w="40px" px={2}>
                                  {hasBreakdowns && (
                                    <Icon
                                      color={textSecondary}
                                      boxSize={3}
                                      transition="transform 0.2s"
                                      transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                                    >
                                      <FontAwesomeIcon icon={faChevronRight} />
                                    </Icon>
                                  )}
                                </Td>
                                <Td>
                                  <HStack>
                                    <Box p={2} bg="pink.100" borderRadius="lg">
                                      <Icon color="pink.500" boxSize={3}>
                                        <FontAwesomeIcon icon={faRectangleAd} />
                                      </Icon>
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="600">{ad.name}</Text>
                                      {hasBreakdowns && (
                                        <Text fontSize="xs" color={textSecondary}>
                                          {ad.breakdowns.length} placement{ad.breakdowns.length !== 1 ? 's' : ''}
                                        </Text>
                                      )}
                                    </VStack>
                                  </HStack>
                                </Td>
                                <Td display={{ base: 'none', lg: 'table-cell' }}>
                                  <Badge colorScheme="purple" variant="subtle" borderRadius="full">{ad.type}</Badge>
                                </Td>
                                <Td isNumeric fontWeight="600">{formatFullNumber(ad.impressions)}</Td>
                                <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(ad.clicks)}</Td>
                                <Td isNumeric>
                                  <Badge colorScheme="brand" variant="subtle" borderRadius="full">{ad.ctr}%</Badge>
                                </Td>
                                <Td>
                                  <Tooltip label="View daily breakdown" placement="left">
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="pink"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDrillDown('ad', ad.id, ad.name);
                                      }}
                                    >
                                      <Icon boxSize={3}>
                                        <FontAwesomeIcon icon={faCrosshairs} />
                                      </Icon>
                                    </Button>
                                  </Tooltip>
                                </Td>
                              </Tr>
                              {isExpanded && ad.breakdowns?.map((breakdown, idx) => (
                                <Tr
                                  key={`ad-${ad.id}-breakdown-${idx}`}
                                  bg={expandedRowBg}
                                  _hover={{ bg: expandedRowHoverBg }}
                                >
                                  <Td w="40px" px={2}></Td>
                                  <Td pl={10}>
                                    <VStack align="start" spacing={0}>
                                      <HStack spacing={2}>
                                        <Box p={1} bg="brand.100" borderRadius="sm">
                                          <Icon color="brand.500" boxSize={2}>
                                            <FontAwesomeIcon icon={faBullseye} />
                                          </Icon>
                                        </Box>
                                        <Text fontSize="sm" fontWeight="500">{breakdown.campaignName}</Text>
                                      </HStack>
                                      <HStack spacing={2} pl={5}>
                                        <Icon color="purple.400" boxSize={2}>
                                          <FontAwesomeIcon icon={faLayerGroup} />
                                        </Icon>
                                        <Text fontSize="xs" color={textSecondary}>
                                          {breakdown.zoneName} ({breakdown.site})
                                        </Text>
                                      </HStack>
                                    </VStack>
                                  </Td>
                                  <Td display={{ base: 'none', lg: 'table-cell' }}></Td>
                                  <Td isNumeric fontSize="sm">{formatFullNumber(breakdown.impressions)}</Td>
                                  <Td isNumeric fontSize="sm" display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(breakdown.clicks)}</Td>
                                  <Td isNumeric>
                                    <Badge colorScheme="pink" variant="outline" borderRadius="full" fontSize="xs">{breakdown.ctr}%</Badge>
                                  </Td>
                                  <Td></Td>
                                </Tr>
                              ))}
                            </>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
                  </CardBody>
                </Card>
              </>
            )}
          </TabPanel>

          {/* By Campaign - Enhanced */}
          <TabPanel px={0}>
            {selectedFilter ? (
              // Filtered daily breakdown view
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faCrosshairs} />
                        </Icon>
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Heading size="md">{selectedFilter.name}</Heading>
                        <Text fontSize="sm" color={textSecondary}>
                          Daily breakdown for this {selectedFilter.type}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                      onClick={clearFilter}
                    >
                      Back
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  {filteredDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredDailyData.map((day, idx) => (
                            <Tr key={idx} _hover={{ bg: hoverBg }} transition="all 0.2s">
                              <Td>
                                <HStack>
                                  <Icon color="brand.400" boxSize={3}>
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                  </Icon>
                                  <Text fontWeight="500">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  </Text>
                                </HStack>
                              </Td>
                              <Td isNumeric fontWeight="600">{formatFullNumber(day.impressions)}</Td>
                              <Td isNumeric>{formatFullNumber(day.clicks)}</Td>
                              <Td isNumeric>
                                <Badge colorScheme="brand" variant="subtle" borderRadius="full">{day.ctr}%</Badge>
                              </Td>
                            </Tr>
                          ))}
                          <Tr bg={hoverBg} fontWeight="bold">
                            <Td><Text fontWeight="700">Total</Text></Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.impressions, 0))}
                            </Td>
                            <Td isNumeric fontWeight="700">
                              {formatFullNumber(filteredDailyData.reduce((sum, d) => sum + d.clicks, 0))}
                            </Td>
                            <Td isNumeric>
                              {(() => {
                                const totalImp = filteredDailyData.reduce((sum, d) => sum + d.impressions, 0);
                                const totalClk = filteredDailyData.reduce((sum, d) => sum + d.clicks, 0);
                                const ctr = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';
                                return <Badge colorScheme="green" variant="solid" borderRadius="full">{ctr}%</Badge>;
                              })()}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </CardBody>
              </Card>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
                  <StatCardCompact
                    icon={faBullseye}
                    label="Total Campaigns"
                    value={campaignData.length}
                    helpText="Active campaigns"
                    color="brand"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faEye}
                    label="Campaign Impressions"
                    value={formatFullNumber(campaignData.reduce((sum, c) => sum + (c.impressions || 0), 0))}
                    helpText="All campaigns"
                    color="cyan"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faMousePointer}
                    label="Campaign Clicks"
                    value={formatFullNumber(campaignData.reduce((sum, c) => sum + (c.clicks || 0), 0))}
                    helpText="Total clicks"
                    color="green"
                    isLoading={isLoading}
                  />
                  <StatCardCompact
                    icon={faPercent}
                    label="Avg Campaign CTR"
                    value={`${campaignData.length > 0 ? (campaignData.reduce((sum, c) => sum + parseFloat(c.ctr || 0), 0) / campaignData.length).toFixed(2) : 0}%`}
                    helpText="Average"
                    color="orange"
                    isLoading={isLoading}
                  />
                </SimpleGrid>

                {/* Campaign Charts */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                  <CampaignComparisonChart data={campaignData} loading={isLoading} />
                  <TopPerformersCard campaigns={campaignData} loading={isLoading} />
                </SimpleGrid>

                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                  <CardBody>
                    {isLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner size="lg" color="brand.500" />
                  </Flex>
                ) : showDailyBreakdown ? (
                  // Daily breakdown view
                  campaignDailyData.length === 0 ? (
                    <Text textAlign="center" color={textSecondary} py={8}>No daily campaign data available</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th w="40px"></Th>
                            <Th>Campaign</Th>
                            <Th>Date</Th>
                            <Th isNumeric>Impressions</Th>
                            <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                            <Th isNumeric>CTR</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {campaignDailyData.map((campaign) => {
                            const isExpanded = expandedCampaigns[campaign.id];
                            const hasDaily = campaign.daily && campaign.daily.length > 0;
                            return (
                              <>
                                <Tr
                                  key={`campaign-daily-${campaign.id}`}
                                  _hover={{ bg: hoverBg }}
                                  transition="all 0.2s"
                                  cursor={hasDaily ? 'pointer' : 'default'}
                                  onClick={() => hasDaily && toggleCampaignExpand(campaign.id)}
                                  fontWeight="600"
                                  bg={hoverBg}
                                >
                                  <Td w="40px" px={2}>
                                    {hasDaily && (
                                      <Icon
                                        color={textSecondary}
                                        boxSize={3}
                                        transition="transform 0.2s"
                                        transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                                      >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                      </Icon>
                                    )}
                                  </Td>
                                  <Td>
                                    <HStack>
                                      <Box p={2} bg="brand.100" borderRadius="lg">
                                        <Icon color="brand.500" boxSize={3}>
                                          <FontAwesomeIcon icon={faBullseye} />
                                        </Icon>
                                      </Box>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="600">{campaign.name}</Text>
                                        {hasDaily && (
                                          <Text fontSize="xs" color={textSecondary}>
                                            {campaign.daily.length} day{campaign.daily.length !== 1 ? 's' : ''} with data
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                  </Td>
                                  <Td>
                                    <Badge colorScheme="gray" variant="subtle" borderRadius="full">Total</Badge>
                                  </Td>
                                  <Td isNumeric fontWeight="600">{formatFullNumber(campaign.totalImpressions)}</Td>
                                  <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(campaign.totalClicks)}</Td>
                                  <Td isNumeric>
                                    <Badge colorScheme="brand" variant="subtle" borderRadius="full">{campaign.ctr}%</Badge>
                                  </Td>
                                </Tr>
                                {isExpanded && campaign.daily?.map((day, idx) => (
                                  <Tr
                                    key={`campaign-${campaign.id}-day-${idx}`}
                                    bg={expandedRowBg}
                                    _hover={{ bg: expandedRowHoverBg }}
                                  >
                                    <Td w="40px" px={2}></Td>
                                    <Td pl={10}></Td>
                                    <Td>
                                      <HStack>
                                        <Icon color="brand.400" boxSize={3}>
                                          <FontAwesomeIcon icon={faCalendarDays} />
                                        </Icon>
                                        <Text fontSize="sm">
                                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </Text>
                                      </HStack>
                                    </Td>
                                    <Td isNumeric fontSize="sm">{formatFullNumber(day.impressions)}</Td>
                                    <Td isNumeric fontSize="sm" display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(day.clicks)}</Td>
                                    <Td isNumeric>
                                      <Badge colorScheme="gray" variant="outline" borderRadius="full" fontSize="xs">{day.ctr}%</Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )
                ) : campaignData.length === 0 ? (
                  <Text textAlign="center" color={textSecondary} py={8}>No campaign data available</Text>
                ) : (
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th w="40px"></Th>
                          <Th>Campaign</Th>
                          <Th isNumeric>Impressions</Th>
                          <Th isNumeric display={{ base: 'none', md: 'table-cell' }}>Clicks</Th>
                          <Th isNumeric>CTR</Th>
                          <Th display={{ base: 'none', lg: 'table-cell' }}>Performance</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {campaignData.map((campaign) => {
                          const maxImpressions = Math.max(...campaignData.map(c => c.impressions || 0));
                          const percentage = maxImpressions > 0 ? ((campaign.impressions || 0) / maxImpressions) * 100 : 0;
                          const isExpanded = expandedCampaigns[campaign.id];
                          const hasZones = campaign.zones && campaign.zones.length > 0;
                          return (
                            <>
                              <Tr
                                key={`campaign-${campaign.id}`}
                                _hover={{ bg: hoverBg }}
                                transition="all 0.2s"
                                cursor={hasZones ? 'pointer' : 'default'}
                                onClick={() => hasZones && toggleCampaignExpand(campaign.id)}
                              >
                                <Td w="40px" px={2}>
                                  {hasZones && (
                                    <Icon
                                      color={textSecondary}
                                      boxSize={3}
                                      transition="transform 0.2s"
                                      transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                                    >
                                      <FontAwesomeIcon icon={faChevronRight} />
                                    </Icon>
                                  )}
                                </Td>
                                <Td>
                                  <HStack>
                                    <Box p={2} bg="brand.100" borderRadius="lg">
                                      <Icon color="brand.500" boxSize={3}>
                                        <FontAwesomeIcon icon={faBullseye} />
                                      </Icon>
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="600">{campaign.name}</Text>
                                      {hasZones && (
                                        <Text fontSize="xs" color={textSecondary}>
                                          {campaign.zones.length} zone{campaign.zones.length !== 1 ? 's' : ''}
                                        </Text>
                                      )}
                                    </VStack>
                                  </HStack>
                                </Td>
                                <Td isNumeric fontWeight="600">{formatFullNumber(campaign.impressions)}</Td>
                                <Td isNumeric display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(campaign.clicks)}</Td>
                                <Td isNumeric>
                                  <Badge colorScheme="brand" variant="subtle" borderRadius="full">{campaign.ctr}%</Badge>
                                </Td>
                                <Td display={{ base: 'none', lg: 'table-cell' }}>
                                  <Progress
                                    value={percentage}
                                    size="sm"
                                    colorScheme="brand"
                                    borderRadius="full"
                                    bg={borderColor}
                                    w="100px"
                                  />
                                </Td>
                              </Tr>
                              {isExpanded && campaign.zones?.map((zone) => (
                                <Tr
                                  key={`campaign-${campaign.id}-zone-${zone.id}`}
                                  bg={expandedRowBg}
                                  _hover={{ bg: expandedRowHoverBg }}
                                >
                                  <Td w="40px" px={2}></Td>
                                  <Td pl={10}>
                                    <HStack>
                                      <Box p={1.5} bg="purple.100" borderRadius="md">
                                        <Icon color="purple.500" boxSize={2.5}>
                                          <FontAwesomeIcon icon={faLayerGroup} />
                                        </Icon>
                                      </Box>
                                      <VStack align="start" spacing={0}>
                                        <Text fontSize="sm" fontWeight="500">{zone.name}</Text>
                                        <Text fontSize="xs" color={textSecondary}>{zone.site}</Text>
                                      </VStack>
                                    </HStack>
                                  </Td>
                                  <Td isNumeric fontSize="sm">{formatFullNumber(zone.impressions)}</Td>
                                  <Td isNumeric fontSize="sm" display={{ base: 'none', md: 'table-cell' }}>{formatFullNumber(zone.clicks)}</Td>
                                  <Td isNumeric>
                                    <Badge colorScheme="purple" variant="outline" borderRadius="full" fontSize="xs">{zone.ctr}%</Badge>
                                  </Td>
                                  <Td display={{ base: 'none', lg: 'table-cell' }}></Td>
                                </Tr>
                              ))}
                            </>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </Card>
              </>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Export Builder Modal */}
      <ExportBuilderModal
        isOpen={isExportOpen}
        onClose={onExportClose}
        pageType="reports"
        data={{
          overview,
          campaignData,
          zoneData,
          adData,
        }}
        dateRange={dateRange}
        filters={{ site: selectedSite, type: typeFilter, filterBots, entity: selectedFilter }}
      />
    </Box>
  );
}

export default ReportsPage;
